// Reading an audit entry back (2026-09-28).
//
// The backend stores full before/after snapshots rather than a pre-computed
// list of what changed. That is the right shape for an audit record — it can
// answer questions nobody thought to ask when the code was written — but it
// means the reader has to do the comparing. Previously the dialog called
// String() on those snapshots and printed "[object Object]", so the one row
// that explains why a fare moved said nothing at all.
//
// Nothing here interprets a fare. These helpers only compare two objects the
// backend already sent and name the keys that differ.

// Duty type travels as a bare integer on the wire. The live master list
// (state.userDutyType.dutyTypeList) is preferred wherever it is loaded; this
// is the fallback so a name still appears if the page has not fetched it.
export const DUTY_TYPE_FALLBACK = {
  4: 'Airport Transfer',
  5: 'Rental',
  6: 'Outstation One Way',
  7: 'Outstation',
};

// The vocabulary the duty slip uses, so the audit trail reads the same way as
// the modal the edit was made in.
const PRICING_MODE_LABEL = {
  defaultRate: 'Rate Card',
  customRate: 'Edited Rates',
};

// Keys worth naming. Anything absent falls through to a readable version of
// the key itself, so an unmapped field still renders sensibly.
const KEY_LABEL = {
  cost: 'Package cost',
  baseRate: 'Base fare',
  // The airport shape carries the per-km rate twice, once on the card and once
  // inside its rate config, under different spellings. Both clear together on a
  // conversion, so naming them identically reads as an accidental duplicate.
  baseRatePerKM: 'Base rate per km',
  base_rate_per_km: 'Base rate per km (rate config)',
  flatAmount: 'Flat amount',
  minKm: 'Minimum km',
  pricingType: 'Pricing basis',
  rateType: 'Rate type',
  duration: 'Duration',
  durationType: 'Duration unit',
  maxDistanceKms: 'Max distance',
  extraKM: 'Extra km',
  extraHrs: 'Extra hours',
  waitingTime: 'Waiting time',
  nightAllowance: 'Night allowance',
  earlyStartAllowance: 'Early start allowance',
  driverAllowance: 'Driver allowance',
  additionalCharges: 'Additional charges',
  surCharge: 'Surcharge',
  platformCharge: 'Platform charge',
  fixedGarageRate: 'Fixed garage rate',
  kilometersIn: 'Garage to pickup',
  kilometersOut: 'Drop to garage',
  distance: 'Distance',
  rate: 'Rate',
  graceValue: 'Grace',
  unitType: 'Unit',
  name: 'Name',
  garageAddress: 'Garage address',
  pricingMode: 'Pricing mode',
  sourceRateCardId: 'Rate card id',
  isMultiDay: 'Multi-day',
  tripType: 'Duty type',
};

// The rate card carries its own row identity and audit columns. They are the
// card's, not this trip's, and they never answer "what changed about the
// price", so they stay out of the comparison.
const IGNORED_KEYS = new Set([
  'id',
  'createdAt',
  'updatedAt',
  'deletedAt',
  'contractId',
  'carType',
  'carTypeId',
  'location',
  'rateCardType',
  'rateCardTypeId',
  // These three travel inside the stored rate card AND as their own entries in
  // valuesChanged. Listing them here as well says the same thing twice, in the
  // middle of a long list where the repetition is least welcome.
  'pricingMode',
  'sourceRateCardId',
  'tripType',
]);

// The stored card nests a `rateConfig` inside a `rateConfig`, so a plain path
// reads "Rate Config · Max distance". That prefix names an implementation
// detail, not anything a reader of a duty slip knows about.
const TRANSPARENT_KEYS = new Set(['rateConfig']);

const humanize = (key) =>
  String(key)
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase());

export const labelForKey = (key) => KEY_LABEL[key] || humanize(key);

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

// "Empty" reads as data loss. A cleared rate card id means the trip stopped
// using a saved card, which is a choice, not an accident.
const EMPTY = '—';

/**
 * One value, as a short string. Objects and arrays are summarised rather than
 * dumped: by the time this is called the caller has already narrowed to the
 * keys that differ, so a nested object only appears when it changed as a whole
 * (added or removed), where a summary is what a reader wants.
 */
export const formatValue = (value, key) => {
  if (value === null || value === undefined || value === '') return EMPTY;

  if (key === 'pricingMode') return PRICING_MODE_LABEL[value] || String(value);
  if (key === 'tripType' || key === 'type') {
    return DUTY_TYPE_FALLBACK[Number(value)] || String(value);
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';

  if (Array.isArray(value)) {
    if (!value.length) return EMPTY;
    const named = value.map((v) => (isPlainObject(v) ? v.name || v.label : v)).filter(Boolean);
    return named.length ? named.join(', ') : `${value.length} items`;
  }

  if (isPlainObject(value)) {
    // Allowances and overage rules are {rate, unitType, graceValue}; the rate
    // is the part a reader is looking for.
    if ('rate' in value) {
      const unit = value.unitType && value.unitType !== 'Rs' ? ` / ${value.unitType}` : '';
      return `${value.rate}${unit}`;
    }
    if ('name' in value) return String(value.name);
    return 'set';
  }

  return String(value);
};

/**
 * Every leaf path where `before` and `after` differ, depth-first, as
 * { path, label, oldValue, newValue }. Keys present on one side only are
 * reported too — a duty-type conversion drops one shape's keys and adds
 * another's, and that is exactly what a reader needs to see.
 */
export const diffObjects = (before, after, trail = []) => {
  const rows = [];
  const a = isPlainObject(before) ? before : {};
  const b = isPlainObject(after) ? after : {};
  const keys = Array.from(new Set([...Object.keys(a), ...Object.keys(b)]));

  keys.forEach((key) => {
    if (IGNORED_KEYS.has(key)) return;
    const oldValue = a[key];
    const newValue = b[key];
    const path = TRANSPARENT_KEYS.has(key) ? trail : [...trail, key];

    // Recurse only while both sides are objects; if one side is missing the
    // whole branch was added or removed, which reads better as one row than as
    // a row per leaf inside it.
    if (isPlainObject(oldValue) && isPlainObject(newValue)) {
      rows.push(...diffObjects(oldValue, newValue, path));
      return;
    }

    if (JSON.stringify(oldValue ?? null) === JSON.stringify(newValue ?? null)) return;

    rows.push({
      path: (path.length ? path : [key]).join('.'),
      label: (path.length ? path : [key]).map(labelForKey).join(' · '),
      oldValue: formatValue(oldValue, key),
      newValue: formatValue(newValue, key),
    });
  });

  return rows;
};

/**
 * One `valuesChanged` entry, ready to render.
 *
 * `rows` is set when the entry's values are objects — the nested lines that
 * say what actually moved inside them. `dutyTypeName` lets the caller pass the
 * live master list so the audit trail names a duty type the same way the rest
 * of the app does.
 */
export const describeChange = (item, { dutyTypeName } = {}) => {
  const { field, label, oldValue, newValue } = item || {};

  if (field === 'type') {
    const name = (v) =>
      (dutyTypeName && dutyTypeName(v)) || DUTY_TYPE_FALLBACK[Number(v)] || formatValue(v);
    return { label: label || 'Duty Type', oldValue: name(oldValue), newValue: name(newValue) };
  }

  if (field === 'sourceRateCardId') {
    // A custom rate has no card to point at. "Not applicable" says that;
    // "Empty" reads as something having gone missing.
    return {
      label: label || 'Rate Card Id',
      oldValue: oldValue ? String(oldValue) : 'Not applicable',
      newValue: newValue ? String(newValue) : 'Not applicable',
    };
  }

  if (isPlainObject(oldValue) || isPlainObject(newValue)) {
    const rows = diffObjects(oldValue, newValue);
    return {
      label: label || labelForKey(field),
      rows,
      // A rate card can be replaced wholesale with an identical one — same
      // figures, different card id — and then there is nothing to list. Say so
      // rather than leave the row blank.
      empty: rows.length === 0,
    };
  }

  return {
    label: label || labelForKey(field),
    oldValue: formatValue(oldValue, field),
    newValue: formatValue(newValue, field),
    // Some writers record a field whether or not it moved — a reassignment
    // always lists Manual Execution, for instance. Flagged rather than hidden:
    // an audit trail should not quietly drop something the backend recorded,
    // but a reader should not have to squint to see it did not change.
    unchanged: JSON.stringify(oldValue ?? null) === JSON.stringify(newValue ?? null),
  };
};

export default describeChange;
